/**
 * @fileoverview Syndicator Showdown — Shared Constants
 * @module shared/constants
 *
 * Single source of truth for all game mechanics constants.
 * Imported by Cloud Functions (require) and React frontend (import).
 */

// ─── Share pricing ────────────────────────────────────────────────────────────

/**
 * Base price scaling factor.
 * Formula: basePrice = SCALE_FACTOR / (odds × fieldSize) × sqrt(raceCount / 10)
 */
export const SCALE_FACTOR = 27500;

/**
 * Tiered equity pricing — Option B (30/25/20/15/10 split).
 * Multipliers apply to baseSharePrice per 1%, based on the player's
 * cumulative holding after the purchase. Calculated 1% at a time
 * so partial-band purchases are always priced correctly.
 *
 * Full purchase (0→100%) averages 1.25× base price.
 */
export const TIER_BANDS = [
  { upTo: 30,  multiplier: 1.00 }, // First 30% at face value
  { upTo: 55,  multiplier: 1.10 }, // Next 25% at 10% premium
  { upTo: 75,  multiplier: 1.25 }, // Next 20% at 25% premium
  { upTo: 90,  multiplier: 1.50 }, // Next 15% at 50% premium
  { upTo: 100, multiplier: 2.00 }, // Final 10% at 100% premium
];

// ─── Slider ───────────────────────────────────────────────────────────────────

/** Equity slider range — whole percentage points, 0 through 100. */
export const SLIDER_MIN  = 0;
export const SLIDER_MAX  = 100;
export const SLIDER_STEP = 1;

// ─── Bankroll mechanics ────────────────────────────────────────────────────────

/**
 * Controls the per-race floor spend calculation.
 * floor = avgSpendPerRace × FLOOR_DIVISOR / (FLOOR_DIVISOR + raceCount)
 */
export const FLOOR_DIVISOR = 20;

/** Fallback when admin hasn't configured the slate average spend. */
export const DEFAULT_BANKROLL_PER_RACE = 100000;

// ─── Compliance thresholds ─────────────────────────────────────────────────────

/**
 * Minimum number of runners a player must select in each race.
 * Ensures engagement with each leg rather than token participation.
 */
export const MIN_RUNNERS_PER_RACE = 1;

/**
 * Maximum fraction of a field's runners a player may hold equity in.
 * 0.5 = player may hold equity in at most 50% of the active field.
 * Enforced at purchase time (BuyModal) and at submission (EntryContext compliance).
 * Change to 1.0 to disable the cap entirely.
 */
export const MAX_FIELD_COVERAGE = 0.5;

// ─── Default prize pool ────────────────────────────────────────────────────────

/**
 * Default virtual prize distribution when admin hasn't configured a custom pool.
 * Values in virtual dollars. Overridden per-slate in game_slates/{slateId}.
 */
export const DEFAULT_PRIZE_POOL = {
  first:  500000,
  second: 200000,
  third:  100000,
  fourth:  60000,
  fifth:   40000,
};

// ─── Race integrity ─────────────────────────────────────────────────────────

/**
 * Hash algorithm used by functions/src/integrity/generateHashManifest.js to
 * build the per-race SHA-256 selection/master hash manifest players use to
 * independently verify their locked allocation was never altered post-jump.
 */
export const HASH_ALGORITHM = 'sha256';